export type ChatBlock =
  | { kind: "text"; content: string }
  | { kind: "widget"; widgetType: string; data: unknown };

export interface ParsedChatResponse {
  blocks: ChatBlock[];
  hasIncompleteWidget: boolean;
  incompleteWidgetType: string | null;
}

// Module-load marker — confirms this version is active in the browser.
console.log("[parse-chat-response] v3 loaded");

// Fix the most common AI JSON mistake: using `}` to close an array `[` or
// `]` to close an object `{`. Walks the string tracking the bracket stack
// and swaps mismatched closers in-place.
function repairJson(text: string): string {
  const stack: string[] = [];
  let inStr = false;
  let esc = false;
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (esc) { esc = false; out += c; continue; }
    if (c === "\\" && inStr) { esc = true; out += c; continue; }
    if (c === '"') { inStr = !inStr; out += c; continue; }
    if (!inStr) {
      if (c === "{") { stack.push("{"); out += c; }
      else if (c === "[") { stack.push("["); out += c; }
      else if (c === "}" || c === "]") {
        const expected = stack[stack.length - 1] === "[" ? "]" : "}";
        out += expected;
        stack.pop();
      } else {
        out += c;
      }
    } else {
      out += c;
    }
  }
  return out;
}

// Brace-counts a JSON object starting at `start` inside `text`.
// Returns the index of the character AFTER the closing `}`, or -1 if the
// object is incomplete (ran out of characters before depth reached 0 again).
function findJsonEnd(text: string, start: number): number {
  let depth = 0;
  let inStr = false;
  let esc = false;
  let i = start;
  while (i < text.length) {
    const c = text[i];
    if (esc) { esc = false; i++; continue; }
    if (c === "\\") { esc = true; i++; continue; }
    if (c === '"') { inStr = !inStr; i++; continue; }
    if (!inStr) {
      if (c === "{") depth++;
      else if (c === "}") {
        depth--;
        if (depth === 0) return i + 1; // one past the closing `}`
      }
    }
    i++;
  }
  return -1; // incomplete
}

// Attempt to recover a truncated JSON object by closing any open brackets and
// braces. Returns the parsed value, or null if it can't be salvaged.
function tryRecoverJson(fragment: string): unknown | null {
  const stack: string[] = [];
  let inStr = false;
  let esc = false;
  for (const c of fragment) {
    if (esc) { esc = false; continue; }
    if (c === "\\") { esc = true; continue; }
    if (c === '"') { inStr = !inStr; continue; }
    if (!inStr) {
      if (c === "{" || c === "[") stack.push(c === "{" ? "}" : "]");
      else if (c === "}" || c === "]") stack.pop();
    }
  }
  // Close any dangling string, then close all open brackets in reverse order.
  const closing = (inStr ? '"' : "") + stack.reverse().join("");
  try { return JSON.parse(fragment + closing); } catch { return null; }
}

// Widget types we deliberately render at most once per message. The backend
// deterministically emits a recommendations widget on the first turn; if the
// LLM ignores the suppression note and emits its own (often malformed /
// truncated), we silently drop it rather than letting the raw token leak
// into the chat as plain text.
const SINGLETON_WIDGET_TYPES = new Set(["recommendations"]);

// Matches {{type:{json}}}. The JSON body is brace-counted so nested objects
// survive. When streaming, a `{{` without its closing `}}` signals an
// in-flight widget — callers render a shimmer placeholder until it completes.
export function parseChatResponse(text: string, isStreaming: boolean): ParsedChatResponse {
  const blocks: ChatBlock[] = [];
  const renderedWidgetTypes = new Set<string>();
  let i = 0;
  let textStart = 0;
  let hasIncompleteWidget = false;
  let incompleteWidgetType: string | null = null;

  const flushText = (end: number) => {
    if (end > textStart) {
      const content = text.slice(textStart, end);
      if (content) blocks.push({ kind: "text", content });
    }
  };

  while (i < text.length) {
    if (text[i] === "{" && text[i + 1] === "{") {
      // Read `type:` up to the first `:`.
      let j = i + 2;
      const typeStart = j;
      while (j < text.length && text[j] !== ":" && text[j] !== "}") j++;
      if (text[j] !== ":") {
        i = j;
        continue;
      }
      const widgetType = text.slice(typeStart, j).trim();
      j++; // skip ':'

      // Skip optional whitespace between `:` and the JSON object.
      while (j < text.length && (text[j] === " " || text[j] === "\n" || text[j] === "\r" || text[j] === "\t")) j++;

      if (text[j] !== "{") {
        i = j;
        continue;
      }

      // Brace-count the JSON body.
      const jsonStart = j;
      const jsonEnd = findJsonEnd(text, jsonStart);

      // Singleton widgets (e.g. recommendations) are deterministically emitted
      // by the backend exactly once per message. If the LLM emits a duplicate
      // — complete OR truncated — drop it entirely so the raw token never
      // shows up as plain text in the chat.
      const isDuplicateSingleton =
        SINGLETON_WIDGET_TYPES.has(widgetType) && renderedWidgetTypes.has(widgetType);

      if (jsonEnd === -1) {
        if (isStreaming) {
          // In-flight — show shimmer until the full token arrives (unless
          // this is a duplicate singleton, in which case just drop it).
          flushText(i);
          if (!isDuplicateSingleton) {
            hasIncompleteWidget = true;
            incompleteWidgetType = widgetType;
          }
          textStart = text.length;
          i = text.length;
          break;
        }
        // Stream finished but JSON is truncated (AI hit output limit).
        if (isDuplicateSingleton) {
          // Duplicate — silently consume the rest of the token (everything
          // up to end of input) without rendering anything.
          flushText(i);
          textStart = text.length;
          i = text.length;
          continue;
        }
        // Try to recover: close any open arrays/objects and parse the fragment.
        const fragment = text.slice(jsonStart);
        const recovered = tryRecoverJson(fragment);
        if (recovered !== null) {
          flushText(i);
          blocks.push({ kind: "widget", widgetType, data: recovered });
          renderedWidgetTypes.add(widgetType);
          textStart = text.length;
          i = text.length;
        } else {
          // Recovery failed (e.g. stream cut off mid-string inside a nested
          // object). Drop the entire token rather than leaking raw `{{type:{…`
          // JSON into the chat as plain text — the user can't act on it and
          // it looks broken. Flush the text before the token, then skip to
          // end of input.
          flushText(i);
          textStart = text.length;
          i = text.length;
        }
        continue;
      }

      // jsonEnd is one past the JSON's closing `}`.
      // Skip optional whitespace/newlines before the closing `}}`.
      let k = jsonEnd;
      while (k < text.length && (text[k] === " " || text[k] === "\n" || text[k] === "\r" || text[k] === "\t")) k++;

      // The AI inconsistently emits `}}`, `}`, or nothing after the JSON body.
      // Since findJsonEnd already brace-counted the JSON precisely, a valid JSON
      // body is sufficient proof of a genuine widget — consume any trailing `}}`
      // or `}` that the AI added, but don't require them.
      // Exception: while streaming, no closing brace(s) yet means the AI hasn't
      // finished — show a shimmer until the full token arrives.
      let afterWidget: number;
      if (text[k] === "}" && text[k + 1] === "}") {
        afterWidget = k + 2;
        // Consume any extra trailing `}` the AI may have added
        while (text[afterWidget] === "}") afterWidget++;
      } else if (text[k] === "}") {
        afterWidget = k + 1; // AI emitted one `}` instead of two
      } else if (isStreaming) {
        // Closer not arrived yet — render shimmer placeholder.
        flushText(i);
        hasIncompleteWidget = true;
        incompleteWidgetType = widgetType;
        textStart = text.length;
        i = text.length;
        break;
      } else {
        afterWidget = jsonEnd; // AI omitted closer entirely — use JSON end
      }

      const rawJson = text.slice(jsonStart, jsonEnd);

      flushText(i);
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(rawJson);
      } catch {
        // Second attempt: fix mismatched closers (e.g. `}` closing an array `[`).
        const repaired = repairJson(rawJson);
        try {
          parsed = JSON.parse(repaired);
        } catch {
          // Third attempt: repair fixed the closer but left the outer object
          // unclosed (e.g. notes `}→]` repair consumed the only `}`, so the
          // wrapping `{` has no closer). Close remaining open brackets.
          const recovered = tryRecoverJson(repaired);
          if (recovered !== null) parsed = recovered;
        }
      }
      if (parsed !== null) {
        if (isDuplicateSingleton) {
          // Drop silently — we already rendered one of these.
          flushText(i);
        } else {
          blocks.push({ kind: "widget", widgetType, data: parsed });
          renderedWidgetTypes.add(widgetType);
        }
      } else {
        if (isDuplicateSingleton) {
          // Malformed duplicate — drop the token text too, don't leak it.
          flushText(i);
        } else {
          blocks.push({ kind: "text", content: text.slice(i, afterWidget) });
        }
      }
      textStart = afterWidget;
      i = afterWidget;
      continue;
    }
    i++;
  }

  flushText(text.length);

  return { blocks, hasIncompleteWidget, incompleteWidgetType };
}

// One-line preview for chat list rows: strip widget tokens.
export function stripWidgetTokens(text: string): string {
  return text.replace(/\{\{\w+:\{[\s\S]*?\}\}\}/g, "").replace(/\s+/g, " ").trim();
}
